/**
 * WindBarbMapSlider
 *
 * Displays a wind barb map showing wind speed and direction at all stations
 * as a slideshow slide. Uses pigeon-maps (no Leaflet) with language support.
 */
import React, { useState, useEffect, useMemo } from "react";
import { Map, ZoomControl } from "pigeon-maps";
import {
  fitLatLngBounds,
  kioskMapOverlayScale,
  kioskMapTileAttributionHtml,
  kioskMapTileProvider,
  useKioskPigeonMapSize,
} from "../../utils/kioskPigeonMapUtils";
import { PIGEON_MAP_LIMIT_BOUNDS_FULL_VIEWPORT } from "../../utils/constants";
import { KioskCenteredHtmlOverlay } from "../KioskPigeonOverlays";
import { CircularProgress, Box, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../../../context/TranslationContext";
import {
  createUrlFriendlyName,
  findStationByNameOrId,
} from "../../../../utils/rawFunction";
import { useQuery } from "react-query";
import { languageService } from "../../../../context/languageService";
import { useWeatherStationsWindMap } from "../../../../services/useWeatherStationsWindMap";
import { refetchIntervalFn } from "../../utils/kioskRefetch";

const DEFAULT_CENTER = [72.49408333333334, -81.69515];
const DEFAULT_ZOOM = 5.2;
const WIND_LEGEND_GRADIENT =
  "linear-gradient(to right, rgb(98, 113, 184), rgb(98, 113, 184), rgb(98, 113, 184), rgb(98, 113, 184), rgb(61, 110, 163), rgb(74, 148, 170), rgb(74, 146, 148), rgb(77, 142, 124), rgb(76, 164, 76), rgb(103, 164, 54), rgb(162, 135, 64), rgb(162, 109, 92), rgb(141, 63, 92), rgb(151, 75, 145), rgb(95, 100, 160), rgb(91, 136, 161), rgb(91, 136, 161))";
const WIND_SPEED_COLOR_STEPS = [
  {
    max: 5,
    name: "0-4 kt",
    color: "rgb(98, 113, 184)",
    gradient: "rgba(98, 113, 184, 0.35)",
  },
  {
    max: 10,
    name: "5-9 kt",
    color: "rgb(61, 110, 163)",
    gradient: "rgba(61, 110, 163, 0.35)",
  },
  {
    max: 20,
    name: "10-19 kt",
    color: "rgb(74, 146, 148)",
    gradient: "rgba(74, 146, 148, 0.35)",
  },
  {
    max: 30,
    name: "20-29 kt",
    color: "rgb(76, 164, 76)",
    gradient: "rgba(76, 164, 76, 0.35)",
  },
  {
    max: 40,
    name: "30-39 kt",
    color: "rgb(162, 135, 64)",
    gradient: "rgba(162, 135, 64, 0.35)",
  },
  {
    max: 60,
    name: "40-59 kt",
    color: "rgb(141, 63, 92)",
    gradient: "rgba(141, 63, 92, 0.35)",
  },
  {
    max: Number.POSITIVE_INFINITY,
    name: "60+ kt",
    color: "rgb(91, 136, 161)",
    gradient: "rgba(91, 136, 161, 0.35)",
  },
];
const WIND_LEGEND_STEPS_KT = [0, 5, 10, 20, 30, 40, 60];

const getWindCategory = (speedKnots) => {
  const speed = Number.isFinite(speedKnots) ? speedKnots : 0;
  return (
    WIND_SPEED_COLOR_STEPS.find((step) => speed < step.max) ||
    WIND_SPEED_COLOR_STEPS[WIND_SPEED_COLOR_STEPS.length - 1]
  );
};

export default function WindBarbMapSlider({ config, language, ...props }) {
  const mapConfig = config?.config?.map;
  const center = Array.isArray(mapConfig?.center)
    ? [parseFloat(mapConfig.center[0]), parseFloat(mapConfig.center[1])]
    : DEFAULT_CENTER;
  const zoom = mapConfig?.zoom ? parseFloat(mapConfig.zoom) : DEFAULT_ZOOM;
  const bounds = Array.isArray(mapConfig?.bounds)
    ? [
        [
          parseFloat(mapConfig.bounds[0][0]),
          parseFloat(mapConfig.bounds[0][1]),
        ],
        [
          parseFloat(mapConfig.bounds[1][0]),
          parseFloat(mapConfig.bounds[1][1]),
        ],
      ]
    : undefined;

  const [mapContainerRef, mapSize] = useKioskPigeonMapSize();
  const { translate, setCurrentLanguage } = useTranslation();
  const navigate = useNavigate();
  const [currentZoom, setCurrentZoom] = useState(zoom);
  const [mapCenter, setMapCenter] = useState(center);
  const [mapZoom, setMapZoom] = useState(zoom);
  const [speedUnit, setSpeedUnit] = useState("kt");

  const toSelectedUnit = (speedKnots) =>
    speedUnit === "km/h" ? speedKnots * 1.852 : speedKnots;

  const formatLegendTick = (speedKnots) =>
    `${Math.round(toSelectedUnit(speedKnots))}`;

  const toggleSpeedUnit = () => {
    setSpeedUnit((prev) => (prev === "kt" ? "km/h" : "kt"));
  };

  useEffect(() => {
    if (language) {
      const currentUserLanguage = languageService.getPreferredLanguage();
      const languageToUse =
        language === "inuktitut" && currentUserLanguage === "syllabics"
          ? "syllabics"
          : language;
      console.log(
        `WindBarbMapSlider: Setting language from ${language} to ${languageToUse} (user pref: ${currentUserLanguage})`
      );
      setCurrentLanguage(languageToUse);
    }
  }, [language, setCurrentLanguage]);

  const {
    data: windStations,
    isLoading: loading,
    isFetching,
  } = useWeatherStationsWindMap({ enabled: true });

  useQuery(["windBarbMapRefetch"], () => Promise.resolve(), {
    refetchInterval: refetchIntervalFn,
    refetchIntervalInBackground: true,
    onSuccess: () => {},
  });

  useEffect(() => {
    if (bounds && mapSize.width > 0 && mapSize.height > 0) {
      const fitted = fitLatLngBounds(bounds, mapSize.width, mapSize.height, 3, 10);
      if (fitted) {
        setMapCenter(fitted.center);
        setMapZoom(fitted.zoom);
        setCurrentZoom(fitted.zoom);
      }
    } else {
      setMapCenter(center);
      setMapZoom(zoom);
      setCurrentZoom(zoom);
    }
  }, [bounds, mapSize.width, mapSize.height, center, zoom]);

  const filteredStations = windStations || [];
  const showLoadingIndicator = loading || isFetching;

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleStationClick = (stationId) => {
    const station = findStationByNameOrId(stationId);
    if (station) {
      const urlFriendlyName = createUrlFriendlyName(station.name);
      navigate(`/${urlFriendlyName}`);
    }
  };

  const createWindBarbIconHtml = (
    windBarbIcon,
    windDirection,
    windSpeedKnots,
    stationName,
    info,
    hasError = false,
    status,
    hasValidData = true
  ) => {
    const zoomFactor = kioskMapOverlayScale(currentZoom, DEFAULT_ZOOM);
    const baseSize = Math.max(24, Math.min(140, 80 * zoomFactor));
    const fontSize = Math.max(8, Math.min(16, 10 * Math.min(zoomFactor, 1.35)));
    // Match WindBarbMap: smaller type for text-only markers (Not Available / Non-Operational)
    const textOnlyNameFontSize = Math.max(
      6,
      Math.min(14, Math.round(baseSize * 0.085))
    );
    const textOnlyStatusFontSize = Math.max(
      5,
      Math.min(11, Math.round(baseSize * 0.068))
    );

    const windCategory = getWindCategory(windSpeedKnots || 0);
    const windColor = windCategory.color;
    const gradientColor = windCategory.gradient;

    let statusText = "";
    let textColor = "blue";
    let opacity = 1;

    if (status === "Non-Operational") {
      statusText = "Non-Operational";
      textColor = "#ad4242";
      opacity = 0.6;
    } else if (status === "Warning") {
      statusText = "Warning";
      textColor = "#d4a012";
      opacity = 0.7;
    } else if (!hasValidData || hasError) {
      statusText = "Not Available";
      textColor = "#666666";
      opacity = 0.7;
    } else {
      statusText = info || "Operational";
      textColor = "blue";
    }

    const windBarbPath = `/Qulle-wind-barbs/${windBarbIcon}.svg`;

    if (status === "Non-Operational" || !hasValidData || hasError) {
      const displayStatus =
        status === "Non-Operational" ? "(Non-Operational)" : "(Not Available)";

      return `
        <div style='
          text-align: center;
          white-space: nowrap;
        '>
          <div style='
            font-size: ${textOnlyNameFontSize}px; 
            color: #333;
            font-weight: 500;
            text-shadow: 
              -1px -1px 0 #fff,
              1px -1px 0 #fff,
              -1px 1px 0 #fff,
              1px 1px 0 #fff,
              0 0 3px rgba(255,255,255,0.8);
            line-height: 1.2;
          '>
            ${translate(stationName)}
          </div>
          <div style='
            font-size: ${textOnlyStatusFontSize}px; 
            color: ${status === "Non-Operational" ? "#ad4242" : "#666"};
            font-weight: 500;
            text-shadow: 
              -1px -1px 0 #fff,
              1px -1px 0 #fff,
              -1px 1px 0 #fff,
              1px 1px 0 #fff,
              0 0 3px rgba(255,255,255,0.8);
            margin-top: ${Math.max(2, Math.round(textOnlyNameFontSize * 0.15))}px;
          '>
            ${displayStatus}
          </div>
        </div>
      `;
    }

    return `
      <div style='text-align: center;'>
        <div style='position: relative; display: inline-block;'>
          <div style='
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: ${baseSize * 1.5}px;
            height: ${baseSize * 1.5}px;
            background: radial-gradient(circle, ${windColor} 0%, ${gradientColor} 30%, transparent 70%);
            border-radius: 50%;
            z-index: 0;
          '></div>
          <div style='
            position: relative;
            transform: rotate(${windDirection || 0}deg);
            z-index: 1;
          '>
            <img 
              src='${windBarbPath}' 
              style='
                width: ${baseSize}px; 
                height: ${baseSize}px; 
                opacity: ${opacity};
                filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
                display: block;
              '
              onerror="this.style.display='none'"
            />
          </div>
        </div>
        <div style='
          margin-top: ${Math.max(6, Math.round(baseSize * 0.08))}px;
          max-width: min(160px, 42vw);
          margin-left: auto;
          margin-right: auto;
          font-size: ${fontSize}px;
          color: #333;
          font-weight: 600;
          line-height: 1.2;
          text-align: center;
          text-shadow:
            -1px -1px 0 #fff,
            1px -1px 0 #fff,
            -1px 1px 0 #fff,
            1px 1px 0 #fff,
            0 0 3px rgba(255,255,255,0.9);
        '>
          ${translate(stationName)}
        </div>
      </div>
    `;
  };

  const attributionEl = useMemo(
    () => (
      <span dangerouslySetInnerHTML={{ __html: kioskMapTileAttributionHtml }} />
    ),
    []
  );

  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        width: "100%",
        "& .kiosk-pigeon-map-root": {
          width: "100%",
          height: "100%",
        },
      }}
    >
      <Box
        ref={mapContainerRef}
        className="kiosk-pigeon-map-root"
        sx={{ height: "100%", width: "100%", position: "relative" }}
      >
        <Map
          width={mapSize.width}
          height={mapSize.height}
          center={mapCenter}
          zoom={mapZoom}
          animate={false}
          provider={kioskMapTileProvider}
          attribution={attributionEl}
          attributionPrefix={false}
          minZoom={3}
          maxZoom={10}
          limitBounds={PIGEON_MAP_LIMIT_BOUNDS_FULL_VIEWPORT}
          boxClassname="kiosk-pigeon-map-tiles"
          onBoundsChanged={({ center: c, zoom: z }) => {
            setMapCenter(c);
            setMapZoom(z);
            setCurrentZoom(z);
          }}
        >
          {filteredStations.map((station) => {
            const hasValidWindSpeed =
              station.windSpeedQc === undefined || station.windSpeedQc === 100;
            const hasValidWindDirection =
              station.windDirectionQc === undefined ||
              station.windDirectionQc === 100;
            const hasValidData =
              hasValidWindSpeed &&
              hasValidWindDirection &&
              station.windSpeed !== null &&
              station.windDirection !== null;

            const html = createWindBarbIconHtml(
              station.windBarbIcon,
              station.windDirection,
              station.windSpeedKnots,
              station.name,
              station.info,
              station.hasError,
              station.status,
              hasValidData
            );

            return (
              <KioskCenteredHtmlOverlay
                key={station.id}
                anchor={[station.lat, station.lon]}
                html={html}
                onClick={() => handleStationClick(station.id)}
              />
            );
          })}
          <ZoomControl
            style={{ top: "auto", left: "auto", bottom: 16, right: 16 }}
          />
        </Map>
      </Box>

      <Box
        sx={{
          position: "absolute",
          ...(isMobile
            ? { bottom: "0", left: "0", right: "0" }
            : { top: "40px", right: "20px" }),
          zIndex: 1000,
          width: isMobile ? "100%" : "340px",
          maxWidth: "100%",
          backgroundColor: isMobile ? "transparent" : "#ffffff",
          borderRadius: "14px",
          border: isMobile ? "none" : "1px solid rgba(15, 23, 42, 0.12)",
          padding: isMobile ? "0" : "10px",
          boxShadow: isMobile ? "none" : "0 4px 12px rgba(15, 23, 42, 0.16)",
          "& .rhbottom__legend": {
            display: "flex",
            alignItems: "center",
            height: isMobile ? "18px" : "26px",
            width: "100%",
            borderRadius: isMobile ? "0" : "13px",
            overflow: "hidden",
            background: WIND_LEGEND_GRADIENT,
            boxShadow: isMobile ? "none" : "0 2px 8px rgba(15, 23, 42, 0.22)",
          },
          "& .rhbottom__legend > span": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "#ffffff",
            fontSize: isMobile ? "0.46rem" : "0.68rem",
            fontWeight: 600,
            lineHeight: 1,
            textShadow: "0 1px 2px rgba(0, 0, 0, 0.35)",
            userSelect: "none",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 0.5,
            mb: 0.7,
            px: isMobile ? 0.5 : 0,
          }}
        >
          <Typography
            sx={{
              color: isMobile ? "#f8fafc" : "#0f172a",
              fontSize: isMobile ? "0.58rem" : "0.68rem",
              fontWeight: 700,
              lineHeight: 1,
              userSelect: "none",
              textShadow: isMobile ? "0 1px 2px rgba(0,0,0,0.5)" : "none",
            }}
          >
            {translate("Wind Speed")}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {["kt", "km/h"].map((unit) => (
              <Button
                key={unit}
                size="small"
                onClick={() => setSpeedUnit(unit)}
                sx={{
                  minWidth: isMobile ? "40px" : "48px",
                  height: isMobile ? "20px" : "22px",
                  px: isMobile ? 0.75 : 1,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "0.55rem" : "0.62rem",
                  fontWeight: 700,
                  color: speedUnit === unit ? "#ffffff" : "#334155",
                  backgroundColor: speedUnit === unit ? "#000000" : "#ffffff",
                  border:
                    speedUnit === unit
                      ? "1px solid #000000"
                      : "1px solid rgba(15, 23, 42, 0.24)",
                  "&:hover": {
                    backgroundColor:
                      speedUnit === unit
                        ? "#000000"
                        : "rgba(15, 23, 42, 0.06)",
                  },
                }}
              >
                {unit}
              </Button>
            ))}
          </Box>
        </Box>
        <div
          className="rhbottom__legend clickable metric-legend noselect tooltip--left"
          onClick={isMobile ? toggleSpeedUnit : undefined}
          role={isMobile ? "button" : undefined}
          aria-label={isMobile ? "Toggle wind speed units" : undefined}
          title={isMobile ? `Tap to switch unit (${speedUnit})` : undefined}
          style={isMobile ? { cursor: "pointer" } : undefined}
        >
          <span style={{ width: "12.5%" }}>{speedUnit}</span>
          {WIND_LEGEND_STEPS_KT.map((step) => (
            <Box key={step} component="span" style={{ width: "12.5%" }}>
              {formatLegendTick(step)}
            </Box>
          ))}
        </div>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.35,
            color: isMobile ? "#e2e8f0" : "#334155",
            fontSize: isMobile ? "0.56rem" : "0.64rem",
            textAlign: "center",
            textShadow: isMobile ? "0 1px 2px rgba(0,0,0,0.45)" : "none",
            userSelect: "none",
            px: isMobile ? 0.5 : 0,
          }}
        >
          {translate("Arrow points to where wind is blowing to")}
        </Typography>
      </Box>

      {showLoadingIndicator && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            padding: "20px",
            borderRadius: "10px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          }}
        >
          <CircularProgress size={50} />
          <Box sx={{ mt: 2 }}>Loading Wind Data...</Box>
        </Box>
      )}
    </Box>
  );
}
